// Buku utilities for generating outline and managing content
import linkupService from '../../services/linkupService'
import { MODEL_MODES } from '../../constants/agentConfig'

// Generate buku outline based on query/topic using LLM
export const generateBukuOutline = async (topic, llmService) => {
  let academicContext = ''
  let academicSources = []
  
  try {
    // Lakukan pencarian akademik untuk mendapatkan konteks yang relevan
    console.log(`🔍 Mencari konteks akademik untuk topik buku: ${topic}`);
    const academicResults = await linkupService.getScientificInfo(`${topic} book writing research methodology academic structure`);
    
    if (academicResults && academicResults.sources && academicResults.sources.length > 0) {
      // Pastikan minimal 50 sumber dan maksimal 75 sumber untuk buku
      const minSources = Math.max(50, Math.min(academicResults.sources.length, 50));
      const maxSources = Math.min(75, academicResults.sources.length);
      const sourcesToUse = Math.max(minSources, Math.min(maxSources, academicResults.sources.length));
      
      academicSources = academicResults.sources.slice(0, sourcesToUse);
      
      // Prioritaskan sumber global dan berkualitas tinggi
      academicSources = academicSources.filter(source => {
        const title = source.title?.toLowerCase() || '';
        const snippet = source.snippet?.toLowerCase() || '';
        const url = source.url?.toLowerCase() || '';
        
        // Prioritaskan sumber dari jurnal internasional, universitas ternama, dan organisasi global
        const isGlobalSource = 
          url.includes('doi.org') || 
          url.includes('pubmed') || 
          url.includes('springer') || 
          url.includes('elsevier') || 
          url.includes('wiley') || 
          url.includes('nature.com') || 
          url.includes('science.org') || 
          url.includes('ieee.org') || 
          url.includes('acm.org') || 
          url.includes('jstor.org') || 
          url.includes('cambridge.org') || 
          url.includes('oxford') || 
          url.includes('harvard') || 
          url.includes('mit.edu') || 
          url.includes('stanford.edu') || 
          url.includes('who.int') || 
          url.includes('worldbank.org') || 
          url.includes('un.org') || 
          url.includes('oecd.org') || 
          title.includes('international') || 
          title.includes('global') || 
          title.includes('journal') || 
          title.includes('research');
          
        return isGlobalSource;
      });
      
      // Jika setelah filter masih kurang dari 50, ambil sumber tambahan
      if (academicSources.length < 50) {
        const additionalSources = academicResults.sources
          .filter(source => !academicSources.includes(source))
          .slice(0, 50 - academicSources.length);
        academicSources = [...academicSources, ...additionalSources];
      }
      
      // Pastikan tidak lebih dari 75 sumber
      academicSources = academicSources.slice(0, 75);
      
      const topSources = academicSources.slice(0, 10);
      academicContext = `\n\nKonteks Akademik Referensi (${academicSources.length} sumber global berkualitas tinggi):\n${topSources.map((source, index) => 
        `${index + 1}. ${source.title}\n   ${source.snippet}\n   Sumber: ${source.url}`
      ).join('\n')}`;
      console.log(`✅ Ditemukan ${academicSources.length} sumber akademik global berkualitas tinggi untuk outline buku`);
    }
  } catch (error) {
    console.warn('❌ Error saat mencari konteks akademik:', error.message);
  }
  
  const prompt = `Buatkan OUTLINE (struktur poin-poin) buku untuk topik "${topic}" dalam format JSON dengan struktur berikut:
  {
    "sections": [
      {
        "id": "bab1_pengantar",
        "title": "BAB 1: PENGANTAR",
        "description": "Deskripsi singkat isi bagian",
        "minWords": 5000,
        "isEditable": true,
        "subsections": ["Latar Belakang", "Tujuan Buku", "Manfaat Buku"]
      }
    ],
    "academicSources": ${JSON.stringify(academicSources)}
  }
  
  PENTING: Ini adalah OUTLINE (kerangka/struktur) BUKU, BUKAN konten lengkap!
  
  WAJIB SERTAKAN BAGIAN-BAGIAN BERIKUT (TIDAK BOLEH DIHILANGKAN):
  1. KATA PENGANTAR
  2. DAFTAR ISI
  3. BAB 1: PENGANTAR/PENDAHULUAN
  4. BAB-BAB INTI (minimal 8-12 bab sesuai topik)
  5. BAB PENUTUP/KESIMPULAN
  6. DAFTAR PUSTAKA (id: "daftar_pustaka")
  7. TENTANG PENULIS (id: "tentang_penulis")
  8. INDEKS (id: "indeks")
  
  PERSYARATAN SUMBER REFERENSI:
  - WAJIB menggunakan minimal 50-75 sumber akademik global berkualitas tinggi yang telah disediakan
  - Prioritaskan sumber dari jurnal internasional, universitas ternama, dan organisasi global
  - Setiap bab HARUS merujuk pada sumber-sumber akademik yang relevan
  - Pastikan konten buku didasarkan pada penelitian dan data dari sumber-sumber tersebut
  - Sumber harus dicantumkan dalam format APA di Daftar Pustaka
  
  Pedoman:
  - Ikuti struktur buku profesional standar
  - Berikan ID unik untuk setiap bagian (contoh: kata_pengantar, bab1_pengantar, bab2_konsep_dasar, daftar_pustaka)
  - Sertakan subsections (sub-bagian) untuk setiap bab
  - Description berisi penjelasan singkat apa yang akan dibahas di bagian tersebut
  - Sesuaikan dengan topik: "${topic}"
  - Target total kata: 100.000-150.000 kata (ratusan halaman)
  - Setiap bab inti minimal 8.000-12.000 kata
  - PASTIKAN Daftar Pustaka, Tentang Penulis, dan Indeks SELALU ada di akhir outline
  - Buat struktur yang logis dan mengalir dari dasar ke lanjutan
  - WAJIB mengintegrasikan dan merujuk sumber akademik yang telah disediakan dalam setiap bab${academicContext}
  
  Berikan HANYA JSON tanpa penjelasan tambahan:`
  
  try {
    // Use GENIUS mode for comprehensive book writing
    const response = await llmService.sendMessage(
      [{ role: 'user', content: prompt }],
      { 
        mode: MODEL_MODES.GENIUS,
        searchType: 'academic',
        stream: false,
        consumeCredits: false // Don't consume credits for outline generation
      }
    )
    const cleanedResponse = response.replace(/```json|```/g, '').trim()
    const outline = JSON.parse(cleanedResponse)
    
    if (outline && outline.sections && Array.isArray(outline.sections)) {
      // Pastikan setiap section memiliki ID dan struktur yang benar
      let processedSections = outline.sections.map((section, index) => ({
        id: section.id || `section_${index + 1}`,
        title: section.title,
        description: section.description || '',
        minWords: section.minWords || 5000,
        isEditable: section.isEditable !== false,
        subsections: section.subsections || [],
        topic: topic
      }))
      
      // Validasi dan tambahkan bagian wajib jika tidak ada
      const requiredSections = [
        {
          id: 'daftar_pustaka',
          title: 'DAFTAR PUSTAKA',
          minWords: 0,
          description: 'Daftar referensi dan sumber yang digunakan dalam penulisan buku',
          isEditable: false
        },
        {
          id: 'tentang_penulis',
          title: 'TENTANG PENULIS',
          minWords: 500,
          description: 'Biografi singkat dan kredensial penulis',
          isEditable: true
        },
        {
          id: 'indeks',
          title: 'INDEKS',
          minWords: 0,
          description: 'Indeks kata kunci dan topik penting dalam buku',
          isEditable: true
        }
      ]
      
      // Periksa dan tambahkan bagian yang hilang
      requiredSections.forEach(requiredSection => {
        const exists = processedSections.some(section => section.id === requiredSection.id)
        if (!exists) {
          console.log(`⚠️ Menambahkan bagian yang hilang: ${requiredSection.title}`)
          processedSections.push({
            ...requiredSection,
            topic: topic
          })
        }
      })
      
      return {
        outline: processedSections,
        academicSources: academicSources
      }
    } else {
      throw new Error('Format outline tidak valid')
    }
  } catch (error) {
    console.warn('❌ Error generating outline with LLM, using fallback:', error.message);
    // Fallback ke outline default jika LLM gagal
    return {
      outline: getDefaultBukuOutline(topic),
      academicSources: academicSources
    }
  }
}

// Fallback outline function for books
const getDefaultBukuOutline = (topic) => {
  const baseOutline = [
    {
      id: 'kata_pengantar',
      title: 'KATA PENGANTAR',
      minWords: 800,
      description: 'Ucapan terima kasih dan pengantar dari penulis',
      isEditable: true
    },
    {
      id: 'daftar_isi',
      title: 'DAFTAR ISI',
      minWords: 0,
      description: 'Daftar isi dengan nomor halaman',
      isEditable: false
    },
    {
      id: 'bab1_pengantar',
      title: 'BAB 1: PENGANTAR',
      minWords: 8000,
      description: 'Pengenalan topik, latar belakang, dan tujuan buku',
      isEditable: true,
      subsections: [
        'Latar Belakang',
        'Mengapa Topik Ini Penting',
        'Tujuan dan Manfaat Buku',
        'Cara Menggunakan Buku Ini',
        'Struktur Buku'
      ]
    },
    {
      id: 'bab2_konsep_dasar',
      title: 'BAB 2: KONSEP DASAR',
      minWords: 10000,
      description: 'Penjelasan konsep-konsep fundamental yang perlu dipahami',
      isEditable: true,
      subsections: [
        'Definisi dan Terminologi',
        'Sejarah dan Perkembangan',
        'Prinsip-Prinsip Dasar',
        'Komponen Utama',
        'Studi Kasus Pengantar'
      ]
    },
    {
      id: 'bab3_teori_praktik',
      title: 'BAB 3: TEORI DAN PRAKTIK',
      minWords: 12000,
      description: 'Pembahasan mendalam tentang teori dan aplikasi praktis',
      isEditable: true,
      subsections: [
        'Landasan Teoritis',
        'Model dan Framework',
        'Aplikasi Praktis',
        'Best Practices',
        'Common Pitfalls'
      ]
    },
    {
      id: 'bab4_metodologi',
      title: 'BAB 4: METODOLOGI DAN PENDEKATAN',
      minWords: 10000,
      description: 'Metode dan pendekatan yang dapat diterapkan',
      isEditable: true,
      subsections: [
        'Berbagai Pendekatan',
        'Pemilihan Metode yang Tepat',
        'Langkah-Langkah Implementasi',
        'Tools dan Resources',
        'Evaluasi dan Pengukuran'
      ]
    },
    {
      id: 'bab5_studi_kasus',
      title: 'BAB 5: STUDI KASUS DAN CONTOH',
      minWords: 12000,
      description: 'Analisis kasus nyata dan contoh implementasi',
      isEditable: true,
      subsections: [
        'Kasus Sukses',
        'Kasus Kegagalan dan Pembelajaran',
        'Analisis Komparatif',
        'Lessons Learned',
        'Template dan Checklist'
      ]
    },
    {
      id: 'bab6_teknologi_tools',
      title: 'BAB 6: TEKNOLOGI DAN TOOLS',
      minWords: 10000,
      description: 'Teknologi, tools, dan platform yang mendukung',
      isEditable: true,
      subsections: [
        'Overview Teknologi Terkini',
        'Evaluasi Tools dan Platform',
        'Implementasi Teknologi',
        'Integration Strategies',
        'Future Technology Trends'
      ]
    },
    {
      id: 'bab7_strategi_lanjutan',
      title: 'BAB 7: STRATEGI LANJUTAN',
      minWords: 11000,
      description: 'Strategi tingkat lanjut dan optimisasi',
      isEditable: true,
      subsections: [
        'Advanced Techniques',
        'Optimization Strategies',
        'Scaling dan Growth',
        'Risk Management',
        'Innovation Approaches'
      ]
    },
    {
      id: 'bab8_tantangan_solusi',
      title: 'BAB 8: TANTANGAN DAN SOLUSI',
      minWords: 10000,
      description: 'Identifikasi tantangan umum dan solusinya',
      isEditable: true,
      subsections: [
        'Common Challenges',
        'Problem-Solving Framework',
        'Solution Strategies',
        'Preventive Measures',
        'Crisis Management'
      ]
    },
    {
      id: 'bab9_masa_depan',
      title: 'BAB 9: TREN DAN MASA DEPAN',
      minWords: 9000,
      description: 'Tren terkini dan prediksi masa depan',
      isEditable: true,
      subsections: [
        'Current Trends',
        'Emerging Technologies',
        'Future Predictions',
        'Preparing for Change',
        'Continuous Learning'
      ]
    },
    {
      id: 'bab10_implementasi',
      title: 'BAB 10: PANDUAN IMPLEMENTASI',
      minWords: 12000,
      description: 'Panduan praktis untuk implementasi',
      isEditable: true,
      subsections: [
        'Planning dan Preparation',
        'Step-by-Step Implementation',
        'Monitoring dan Evaluation',
        'Troubleshooting Guide',
        'Success Metrics'
      ]
    },
    {
      id: 'bab11_kesimpulan',
      title: 'BAB 11: KESIMPULAN DAN REKOMENDASI',
      minWords: 6000,
      description: 'Kesimpulan, rekomendasi, dan langkah selanjutnya',
      isEditable: true,
      subsections: [
        'Ringkasan Utama',
        'Key Takeaways',
        'Rekomendasi Praktis',
        'Langkah Selanjutnya',
        'Call to Action'
      ]
    },
    {
      id: 'daftar_pustaka',
      title: 'DAFTAR PUSTAKA',
      minWords: 0,
      description: 'Daftar referensi dan sumber yang digunakan dalam penulisan buku',
      isEditable: false
    },
    {
      id: 'tentang_penulis',
      title: 'TENTANG PENULIS',
      minWords: 500,
      description: 'Biografi singkat dan kredensial penulis',
      isEditable: true
    },
    {
      id: 'indeks',
      title: 'INDEKS',
      minWords: 0,
      description: 'Indeks kata kunci dan topik penting dalam buku',
      isEditable: true
    }
  ]

  // Customize outline based on topic if needed
  return baseOutline.map(section => ({
    ...section,
    topic: topic
  }))
}

// Book types and styles configuration
export const BOOK_TYPES = {
  ACADEMIC: 'academic',
  PRACTICAL: 'practical',
  TUTORIAL: 'tutorial',
  REFERENCE: 'reference',
  BIOGRAPHY: 'biography',
  BUSINESS: 'business',
  SELF_HELP: 'self_help',
  TECHNICAL: 'technical',
  EDUCATIONAL: 'educational',
  RESEARCH: 'research'
}

export const WRITING_STYLES = {
  FORMAL: 'formal',
  CONVERSATIONAL: 'conversational',
  ACADEMIC: 'academic',
  PRACTICAL: 'practical',
  STORYTELLING: 'storytelling',
  INSTRUCTIONAL: 'instructional'
}

export const LANGUAGES = {
  INDONESIAN: 'id',
  ENGLISH: 'en'
}

// Calculate total words from outline
export const calculateTotalWords = (outline) => {
  return outline.reduce((total, section) => total + (section.minWords || 0), 0)
}

// Validate outline meets minimum requirements for a book
export const validateBukuOutline = (outline) => {
  const totalWords = calculateTotalWords(outline)
  const requiredSections = [
    'kata_pengantar',
    'daftar_isi', 
    'daftar_pustaka',
    'tentang_penulis'
  ]
  const missingSections = requiredSections.filter(reqId => 
    !outline.some(section => section.id === reqId)
  )
  
  // Check for minimum number of chapters
  const chapters = outline.filter(section => 
    section.id.startsWith('bab') && section.minWords > 0
  )
  
  return {
    isValid: totalWords >= 80000 && missingSections.length === 0 && chapters.length >= 8,
    totalWords,
    chapterCount: chapters.length,
    missingSections,
    errors: [
      ...(totalWords < 80000 ? [`Minimal 80.000 kata diperlukan untuk buku (saat ini: ${totalWords.toLocaleString()})`] : []),
      ...(chapters.length < 8 ? [`Minimal 8 bab diperlukan (saat ini: ${chapters.length})`] : []),
      ...(missingSections.length > 0 ? [`Bagian wajib yang hilang: ${missingSections.join(', ')}`] : [])
    ]
  }
}

// Parse markdown content and extract citations
export const parseMarkdownContent = (content) => {
  if (!content) return { parsedContent: '', citations: [] }
  
  // Extract citations from content (various formats)
  const citationRegex = /\(([^)]+,\s*\d{4}[a-z]?)\)|\[([^\]]+)\]/g
  const citations = []
  let match
  
  while ((match = citationRegex.exec(content)) !== null) {
    citations.push(match[1] || match[2])
  }
  
  // Remove duplicate citations
  const uniqueCitations = [...new Set(citations)]
  
  return {
    parsedContent: content,
    citations: uniqueCitations
  }
}

// Format academic sources for book bibliography
export const formatSourceToBibliography = (source, index, style = 'apa') => {
  const year = new Date().getFullYear() // Fallback year if not available
  const title = source.title || 'Untitled'
  const url = source.url || '#'
  
  // Extract author from title or use generic format
  const authorMatch = title.match(/^([^.]+)\./)
  const author = authorMatch ? authorMatch[1] : 'Author'
  
  switch (style) {
    case 'apa':
      return {
        id: `ref_${index + 1}`,
        citation: `${author} (${year})`,
        fullReference: `${author}. (${year}). ${title}. Retrieved from ${url}`,
        url: url,
        title: title
      }
    case 'mla':
      return {
        id: `ref_${index + 1}`,
        citation: `${author}`,
        fullReference: `${author}. "${title}." Web. ${new Date().getDate()} ${new Date().toLocaleString('default', { month: 'short' })} ${year}. <${url}>.`,
        url: url,
        title: title
      }
    default:
      return {
        id: `ref_${index + 1}`,
        citation: `${author} (${year})`,
        fullReference: `${author}. (${year}). ${title}. Retrieved from ${url}`,
        url: url,
        title: title
      }
  }
}

// Count words in text content
export const countWords = (text) => {
  if (!text || typeof text !== 'string') return 0
  return text.trim().split(/\s+/).filter(word => word.length > 0).length
}

// Format progress percentage
export const formatProgress = (current, total) => {
  if (total === 0) return 0
  return Math.round((current / total) * 100)
}

// Estimate time remaining for book generation
export const estimateTimeRemaining = (current, total, startTime) => {
  if (current === 0) return null
  
  const elapsed = Date.now() - startTime
  const avgTimePerSection = elapsed / current
  const remaining = (total - current) * avgTimePerSection
  
  const minutes = Math.ceil(remaining / (1000 * 60))
  
  if (minutes < 1) return 'Kurang dari 1 menit'
  if (minutes === 1) return '1 menit'
  if (minutes < 60) return `${minutes} menit`
  
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  
  if (hours === 1 && remainingMinutes === 0) return '1 jam'
  if (hours === 1) return `1 jam ${remainingMinutes} menit`
  if (remainingMinutes === 0) return `${hours} jam`
  
  return `${hours} jam ${remainingMinutes} menit`
}

// Generate book metadata
export const generateBookMetadata = (topic, outline, language = 'id') => {
  const totalWords = calculateTotalWords(outline)
  const estimatedPages = Math.ceil(totalWords / 250) // Assuming 250 words per page
  
  return {
    title: topic,
    totalWords: totalWords,
    estimatedPages: estimatedPages,
    chapterCount: outline.filter(section => section.id.startsWith('bab')).length,
    language: language,
    generatedAt: new Date().toISOString(),
    wordTarget: '100,000-150,000 kata',
    pageTarget: '400-600 halaman'
  }
}